import { ref, useRoute, useRouter } from "@nuxtjs/composition-api";
import mapValues from "lodash/mapValues";
import pick from "lodash/pick";
import { useDataTableFilterOperators } from "~/components/_refactored/conductor/data-table/useDataTableFilterOperators";

export function useDataTableFilter(defaultFilters = []) {
  const route = useRoute();
  const router = useRouter();

  const query = route.value.query;

  const filterEditorModal = ref(null);

  const filterValues = ref(
    query?.filters ? JSON.parse(query.filters) : defaultFilters
  );

  const OPERATORS = useDataTableFilterOperators();

  const defaultValuesByFieldType = {
    checkbox: true,
    number: 0,
    date: new Date().toISOString().split("T")[0],
    text: "",
  };

  const buildDefaultFields = (filter) => {
    if (!filter.fields) {
      return undefined;
    }

    return Object.fromEntries(
      filter.fields.map((field) => [
        field.key,
        field.defaultValue ?? defaultValuesByFieldType[field.fieldType] ?? null,
      ])
    );
  };

  const openAddFilter = (filter) => {
    if (!filterEditorModal.value) {
      throw new Error("Filter Editor Modal not initialized.");
    }

    filterEditorModal.value.open({
      filterId: filter.id,
      operator: filter.operators?.[0] ?? OPERATORS.EQUALS,
      min: 0,
      max: 0,
      items: [],
      value:
        filter.defaultValue ??
        defaultValuesByFieldType[filter.fieldType] ??
        null,
      fields: buildDefaultFields(filter),
    });
  };

  const stringifyFilterValues = (values) => {
    const result = values.map((filterValue) => {
      return {
        ...filterValue,
        items: (filterValue.items ?? [])
          .map((item) =>
            item ? pick(item, ["id", "name", "fullName", "note"]) : null
          )
          .filter((item) => !!item),
        value:
          filterValue.value && typeof filterValue.value === "object"
            ? pick(filterValue.value ?? {}, ["id", "name", "fullName", "note"])
            : filterValue.value,
        fields: filterValue.fields
          ? mapValues(filterValue.fields, (subValue) =>
              subValue && typeof subValue === "object"
                ? pick(subValue, ["id", "name", "fullName", "note"])
                : subValue
            )
          : undefined,
      };
    });

    return JSON.stringify(result);
  };

  const openEditFilter = (filterValue, index) => {
    if (!filterEditorModal.value) {
      throw new Error("Filter Editor Modal not initialized.");
    }
    filterEditorModal.value.open(filterValue, index);
  };

  const addFilter = (filterValue, index) => {
    if (index !== undefined) {
      filterValues.value.splice(index, 1, filterValue);
    } else {
      filterValues.value.push(filterValue);
    }
    router.push({
      query: {
        ...route.value.query,
        filters: stringifyFilterValues(filterValues.value),
        page: 1,
      },
    });
  };

  const removeFilter = (index) => {
    filterValues.value.splice(index, 1);
    router.push({
      query: {
        ...route.value.query,
        filters: stringifyFilterValues(filterValues.value),
        page: 1,
      },
    });
  };

  const removeFilters = () => {
    filterValues.value = [];
    router.push({
      query: {
        ...route.value.query,
        filters: stringifyFilterValues(filterValues.value),
        page: 1,
      },
    });
  };

  function getFilterSubmitData() {
    return filterValues.value.map(
      ({ filterId, operator, min, max, items, value, fields }) => {
        const filter = {
          filterId,
          operator,
        };

        if (fields) {
          filter.value = mapValues(fields, (subValue) =>
            subValue && typeof subValue === "object"
              ? subValue.id ?? null
              : subValue
          );
          return filter;
        }

        switch (operator) {
          case OPERATORS.EQUALS:
          case OPERATORS.NOT_EQUALS:
          case OPERATORS.LIKE:
          case OPERATORS.GREATER_THAN:
          case OPERATORS.LESS_THAN:
          case OPERATORS.GREATER_THAN_OR_EQ:
          case OPERATORS.LESS_THAN_OR_EQ:
            filter.value = typeof value === "object" ? value.id ?? null : value;
            break;
          case OPERATORS.BETWEEN:
            filter.min = min;
            filter.max = max;
            break;
          case OPERATORS.IN:
          case OPERATORS.NOT_IN:
            filter.items = items
              .map((item) => item?.id ?? null)
              .filter((id) => !!id);
            break;
        }

        return filter;
      }
    );
  }

  return {
    filterEditorModal,
    filterValues,
    openAddFilter,
    openEditFilter,
    addFilter,
    removeFilter,
    removeFilters,
    getFilterSubmitData,
  };
}
